import CustomerSchedulingCalendar from '@components/CustomerSchedulingCalendar';
import Button from '@components/DesignLibrary/Button';
import { Maybe } from '@typings/graphql-models';
import { FC, useContext, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import { CartItem } from '@providers/ShoppingCart';
import { useGAClientId } from '@/providers/GoogleAnalytics';
import { useRouter } from 'next/router';
import useNoodleApi from '@/hooks/useNoodleApi';
import { useToast } from '@/hooks';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import * as tsClient from '@tsClient';
import { getUTMParams } from '@helpers/analytics';

type ProductPrice = Pick<ApiModels.Price, 'sessionDuration' | 'sessionInterval'> &
  CartItem & {
    priceDescription?: {
      html?: string | null;
    } | null;
  };

type Product = Pick<ApiModels.Product, 'id' | 'title' | 'isActive'> & {
  productTypes: Array<Pick<ApiModels.ProductType, 'noodleProductType' | 'id' | 'title'>>;
  prices: Array<
    Pick<
      ApiModels.Price,
      | 'price'
      | 'id'
      | 'sessionDuration'
      | 'sessionInterval'
      | 'id'
      | 'currency'
      | 'numBookings'
      | 'price'
      | 'priceSubDescription'
      | 'priceTitle'
      | 'recurringInterval'
      | 'sessionDuration'
      | 'sessionInterval'
      | 'sessionTime'
    > &
      ProductPrice
  >;
  creator?: {
    name?: string | null;
  } | null;
};
type Price = Product['prices'][0];

type Props = {
  creatorTimezone: Maybe<string>;
  customer: Pick<ApiModels.Person, 'id' | 'name' | 'email'> & {
    primaryColour?: { hex: string } | null;
    image?: { url: string } | null;
  };
  priceId: string | null;
  product: Product;
  onClose: () => void;
  selectedTeamMemberPersonId?: string;
};

type TimeSlotProduct = {
  price: Price, sessionDuration: number, sessionTime: string
};

const AssignTimeSlot: FC<Props> = ({
  creatorTimezone,
  customer,
  priceId,
  product,
  onClose,
  selectedTeamMemberPersonId,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTimeSlotProduct, setSelectedTimeSlotProduct] = useState<TimeSlotProduct | null>(null);

  const gaClientId = useGAClientId();
  const router = useRouter();
  const { getData: buyFreeProductFn } = useNoodleApi(tsClient.buyFreeProduct);
  const addToast = useToast();
  const { triggerRefetchBooking } = useContext(DashboardContext);

  const onDateTimeSelect = (selected: TimeSlotProduct): Promise<void> => {
    // Do this so we can get the price obj too
    setSelectedTimeSlotProduct(selected);
    return new Promise(() => {});
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    if (!selectedTimeSlotProduct) return;

    const { error: buyFreeProductError } = await buyFreeProductFn({
      gaClientId: gaClientId as string,
      personId: customer.id,
      priceId: selectedTimeSlotProduct.price.id,
      productId: selectedTimeSlotProduct.price?.product.id,
      sessionDuration: selectedTimeSlotProduct.sessionDuration,
      sessionTime: selectedTimeSlotProduct.sessionTime,
      source: Array.isArray(router.query.source) ? router.query.source[0] : router.query.source,
      teamMemberPersonId: selectedTeamMemberPersonId,
      utmParams: {
        ...getUTMParams(router.query),
      },
      workflowReferenceId: null,
    });

    if (buyFreeProductError) {
      addToast(useToast.ToastTypeVariants.INFO, 'Cannot purchase consultation product.');
    } else {
      addToast(useToast.ToastTypeVariants.INFO, 'Booking created.');
    }

    triggerRefetchBooking();
    setIsSubmitting(false);
    onClose();
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 24 }}>
      <CustomerSchedulingCalendar
        availabilityTimezone={creatorTimezone}
        priceId={priceId}
        product={product}
        onDateTimeSelect={onDateTimeSelect}
        selectedTeamMemberPersonId={selectedTeamMemberPersonId}
        vertical
        debugKey='AssignTimeSlot'
      />
      <Button fullWidth disabled={!selectedTimeSlotProduct} loading={isSubmitting} variant="primary" size="md" onClick={handleSubmit}>
        Initiate Booking
      </Button>
    </div>
  );
};

export default AssignTimeSlot;
